import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import { Input } from "@/components/Base/Input";
import { Label } from "@/components/Base/Label";
import { Skeleton } from "@/components/Base/Skeleton";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { useStore } from "@/stores/useStore";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { DollarSign, Target, AlertCircle } from "lucide-react";
import { useProfitabilityMetrics } from "@/hooks/queries/useProfitabilityMetrics";

export const ProfitabilityChart: React.FC = () => {
  const { shop } = useStore();
  const [overhead, setOverhead] = useState<number>(30000);

  // Only show for Tekmetric shops
  const isTekmetricShop = shop?.integration === "tekmetric";

  // Use the profitability metrics hook
  const {
    profitabilityBreakdown,
    chartData,
    dateRange,
    isLoading,
    error,
    cached,
    isStale,
    backgroundRefresh,
    computedAt,
  } = useProfitabilityMetrics(overhead);

  // Calculate current month date range for display
  const currentMonth = useMemo(() => {
    if (dateRange?.start) {
      return new Date(dateRange.start);
    }
    return new Date();
  }, [dateRange]);

  // Only show for Tekmetric shops
  if (!isTekmetricShop) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Days to Profit Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="overhead">Monthly Overhead ($)</Label>
              <Input
                id="overhead"
                type="number"
                value={overhead}
                onChange={(e) => setOverhead(Number(e.target.value) || 0)}
                placeholder="Enter monthly overhead"
                className="mt-1"
              />
            </div>
            <div className="text-sm text-gray-500">
              <div>Date Range:</div>
              <Skeleton className="h-4 w-32" />
            </div>
          </div>

          {/* Profit Breakdown Skeleton */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-lg bg-gray-50 p-4">
                <Skeleton className="mb-2 h-8 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>

          {/* Detailed Breakdown Skeleton */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg bg-gray-50 p-4">
                <Skeleton className="mb-2 h-5 w-16" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-28" />
                </div>
              </div>
            ))}
          </div>

          {/* Chart Skeleton */}
          <div className="space-y-4">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-[300px] w-full" />
          </div>

          {/* Summary Skeleton */}
          <div className="rounded-lg bg-gray-50 p-4">
            <Skeleton className="mb-2 h-5 w-32" />
            <div className="space-y-1">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-4 w-64" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Days to Profit Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="overhead">Monthly Overhead ($)</Label>
              <Input
                id="overhead"
                type="number"
                value={overhead}
                onChange={(e) => setOverhead(Number(e.target.value) || 0)}
                placeholder="Enter monthly overhead"
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <div>
              <div className="font-semibold text-red-700">
                Error Loading Profitability Data
              </div>
              <div className="text-sm text-red-600">
                {(error as any)?.message ||
                  "Failed to load profitability metrics. Please try again."}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show message if no data available
  if (!profitabilityBreakdown) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Days to Profit Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="overhead">Monthly Overhead ($)</Label>
              <Input
                id="overhead"
                type="number"
                value={overhead}
                onChange={(e) => setOverhead(Number(e.target.value) || 0)}
                placeholder="Enter monthly overhead"
                className="mt-1"
              />
            </div>
          </div>

          <div className="rounded-lg bg-blue-50 p-4">
            <div className="font-semibold text-blue-700">No Data Available</div>
            <div className="text-sm text-blue-600">
              No profitability data is available for the current period. Please
              ensure you have completed repair orders in the current month.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Days to Profit Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overhead Input */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label htmlFor="overhead">Monthly Overhead ($)</Label>
            <Input
              id="overhead"
              type="number"
              value={overhead}
              onChange={(e) => setOverhead(Number(e.target.value) || 0)}
              placeholder="Enter monthly overhead"
              className="mt-1"
            />
          </div>
          <div className="text-sm text-gray-500">
            <div>Date Range:</div>
            <div className="font-medium">
              {format(startOfMonth(currentMonth), "MMM dd")} -{" "}
              {format(endOfMonth(currentMonth), "MMM dd, yyyy")}
            </div>
            {cached && !isStale && (
              <div className="text-green-600 mt-1 text-xs">
                Cached • {computedAt ? format(new Date(computedAt), "Pp") : ""}
              </div>
            )}
            {isStale && (
              <div className="mt-1 text-xs text-orange-600">
                {backgroundRefresh ? "Refreshing..." : "Stale data"}
              </div>
            )}
          </div>
        </div>

        {/* Profit Breakdown */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-blue-50 p-4">
            <div className="text-2xl font-bold text-blue-600">
              ${profitabilityBreakdown.totalGrossProfit.toFixed(2)}
            </div>
            <div className="text-sm text-blue-600">Total Gross Profit</div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-green-600 text-2xl font-bold">
              ${profitabilityBreakdown.dailyAverageProfit.toFixed(2)}
            </div>
            <div className="text-green-600 text-sm">Daily Average Profit</div>
          </div>

          <div className="rounded-lg bg-orange-50 p-4">
            <div className="text-2xl font-bold text-orange-600">
              {profitabilityBreakdown.daysToProfit === Infinity
                ? "N/A"
                : profitabilityBreakdown.daysToProfit.toFixed(0)}
            </div>
            <div className="text-sm text-orange-600">Days to Profit</div>
          </div>

          <div className="rounded-lg bg-purple-50 p-4">
            <div className="text-2xl font-bold text-purple-600">
              ${profitabilityBreakdown.dailyProfitTarget.toFixed(2)}
            </div>
            <div className="text-sm text-purple-600">Daily Profit Target</div>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 font-semibold">Labor</h4>
            <div className="space-y-1 text-sm">
              <div>
                Revenue: ${profitabilityBreakdown.totalLaborRevenue.toFixed(2)}
              </div>
              <div>
                Cost: ${profitabilityBreakdown.totalLaborCost.toFixed(2)}
              </div>
              <div className="font-semibold">
                Profit: $
                {(
                  profitabilityBreakdown.totalLaborRevenue -
                  profitabilityBreakdown.totalLaborCost
                ).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 font-semibold">Parts</h4>
            <div className="space-y-1 text-sm">
              <div>
                Revenue: ${profitabilityBreakdown.totalPartsRevenue.toFixed(2)}
              </div>
              <div>
                Cost: ${profitabilityBreakdown.totalPartsCost.toFixed(2)}
              </div>
              <div className="font-semibold">
                Profit: $
                {(
                  profitabilityBreakdown.totalPartsRevenue -
                  profitabilityBreakdown.totalPartsCost
                ).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 font-semibold">Sublet</h4>
            <div className="space-y-1 text-sm">
              <div>
                Revenue: ${profitabilityBreakdown.totalSubletRevenue.toFixed(2)}
              </div>
              <div>
                Cost: ${profitabilityBreakdown.totalSubletCost.toFixed(2)}
              </div>
              <div className="font-semibold">
                Profit: $
                {(
                  profitabilityBreakdown.totalSubletRevenue -
                  profitabilityBreakdown.totalSubletCost
                ).toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="space-y-4">
          <h4 className="font-semibold">Cumulative Profit vs Days</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip
                formatter={(value: number) => [
                  `$${value.toFixed(2)}`,
                  "Cumulative Gross Profit",
                ]}
                labelFormatter={(label) => `Day ${label}`}
              />
              <Line
                type="monotone"
                dataKey="cumulativeProfit"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                name="Cumulative Gross Profit"
              />
              {/* Overhead line */}
              <Line
                type="monotone"
                dataKey={() => overhead}
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Overhead"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Summary */}
        <div className="rounded-lg bg-yellow-50 p-4">
          <h4 className="mb-2 flex items-center gap-2 font-semibold">
            <Target className="h-4 w-4" />
            Profit Summary
          </h4>
          <div className="space-y-1 text-sm">
            <div>Monthly Overhead: ${overhead.toFixed(2)}</div>
            <div>
              Current Month Profit: $
              {profitabilityBreakdown.totalGrossProfit.toFixed(2)}
            </div>
            <div>
              Working Days in Current Month:{" "}
              {profitabilityBreakdown.workingDaysInMonth}
            </div>
            <div className="font-semibold">
              Current month&#39;s daily average profit: $
              {profitabilityBreakdown.dailyAverageProfit.toFixed(2)} per day
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
